import { ComponentProps, MouseEventHandler, useCallback, useEffect, useState } from "react";
import clsx from "clsx";
import { useCookies } from "react-cookie";

import { Stores, useGlobalState, useLocalStorage } from "react-migration/lib";

import { ImpersonationBanner, Logo, OverdueInvoiceBanner, OverdueInvoiceModal } from "./components";
import { TrialEndingModal, showTrialEndingModal } from "../billing/components/TrialEndingModal";

export type NavbarProps = ComponentProps<"div">;
export const Navbar = ({ children }: NavbarProps) => {
  return (
    <div
      data-testid="navbar"
      className="atlas-w-screen atlas-bg-neutral-800 atlas-mb-0 atlas-flex atlas-flex-row atlas-flex-wrap atlas-z-[1000] atlas-relative"
    >
      {children}
    </div>
  );
};

export const NavbarMessages = () => {
  const [{ user }] = useGlobalState(Stores.user);
  const [cookies] = useCookies(["impersonation"]);
  const [invoiceMessageActive, setInvoiceMessageActive] = useState(true);
  const [overdueInvoiceNextReminder, setOverdueInvoiceNextReminder] = useLocalStorage(
    "overdueInvoiceNextReminder",
    Date.now()
  );
  const [trialEndingModalVisible, setTrialEndingModalVisible] = useState(
    showTrialEndingModal(user)
  );

  const updateOverdueInvoiceDate = useCallback(() => {
    const TWO_WEEKS = 12096e5;
    setOverdueInvoiceNextReminder(Date.now() + TWO_WEEKS);
  }, [setOverdueInvoiceNextReminder]);

  const handleCloseTrialEndingModal = () => {
    setTrialEndingModalVisible(false);
  };

  const EVERY_HOUR = 3_600_000;
  useEffect(() => {
    const interval = setInterval(() => {
      setTrialEndingModalVisible(showTrialEndingModal(user));
    }, EVERY_HOUR);

    return () => clearInterval(interval);
  }, []);

  return (
    <>
      {user.has_overdue_invoices && invoiceMessageActive && (
        <OverdueInvoiceBanner onClose={() => setInvoiceMessageActive(false)} />
      )}
      {!!cookies.impersonation && <ImpersonationBanner />}
      {user.has_overdue_invoices && overdueInvoiceNextReminder <= Date.now() && (
        <OverdueInvoiceModal closeModal={updateOverdueInvoiceDate} />
      )}

      {trialEndingModalVisible && <TrialEndingModal closeModal={handleCloseTrialEndingModal} />}
    </>
  );
};

export const NavbarContentLeft = ({ children }: ComponentProps<"div">) => {
  return (
    <div
      data-testid="navbar-content-left"
      className="atlas-flex atlas-flex-row atlas-flex-shrink atlas-atlas-flex-4"
    >
      {children}
    </div>
  );
};

export type NavbarItemProps = ComponentProps<
  "div" & {
    active?: boolean;
    onClick?: MouseEventHandler;
    icon: string;
  }
>;
export const NavbarItem = ({ active, children, className, icon, ...rest }: NavbarItemProps) => {
  return (
    <div
      className={clsx(
        "atlas-text-neutral-300 atlas-ease-in-out atlas-duration-500 atlas-mx-1 atlas-cursor-pointer",
        "hover:atlas-text-white hover:atlas-bg-neutral-700",
        "after:atlas-transition-all after:atlas-duration-500 after:atlas-ease-in-out after:atlas-w-0 after:atlas-mx-auto",
        "after:atlas-content-[''] after:atlas-h-1 after:atlas-bg-lightGreen-500 after:atlas-flex after:atlas--mt-1 hover:after:atlas-w-full",
        active && "!atlas-bg-neutral-700 after:atlas-w-full",
        className
      )}
      aria-current={active}
      {...rest}
    >
      <div>
        <i
          className={clsx("atlas-px-3 atlas-py-2 atlas-block atlas-text-2xl sm:atlas-hidden", icon)}
        />
        <p
          className={clsx(
            "atlas-py-3.5 atlas-px-2 atlas-m-0 atlas-whitespace-nowrap sm:atlas-block atlas-hidden",
            active && "atlas-text-white"
          )}
        >
          {children}
        </p>
      </div>
    </div>
  );
};

export type NavbarContentCenterProps = ComponentProps<"div">;
export const NavbarContentCenter = ({ children }: NavbarContentCenterProps) => {
  return (
    <div
      data-testid="navbar-content-center"
      className="atlas-flex atlas-flex-1 atlas-px-2 atlas-justify-center atlas-items-center"
    >
      {children}
    </div>
  );
};

export const NavbarContentRight = ({ children }: ComponentProps<"div">) => {
  return (
    <div
      data-testid="navbar-content-right"
      className="atlas-flex atlas-items-center atlas-justify-end atlas-mr-2 atlas-flex-row"
    >
      {children}
    </div>
  );
};

Navbar.Wrapper = Navbar;
Navbar.Messages = NavbarMessages;
Navbar.Left = NavbarContentLeft;
Navbar.Center = NavbarContentCenter;
Navbar.Right = NavbarContentRight;
Navbar.Logo = Logo;
Navbar.Item = NavbarItem;
